import { useCallback } from 'react';
import type { EntityTypes } from '../../context/DependencyGraphProvider/types';
import DataSourceHelper from '../../helper/DataSourceHelper';
import { getIsDataSourceAnEntityType } from '../../utils/entityDataSource';
import { useGetGlobalAppStateStoreState, useGlobalAppStateStore } from '../app/GlobalAppStateStore';
import { useGetDataSourceRecord } from '../DataSourceRecordStore';
import { useGetGlobalStateStoreState, useGlobalStateStore } from '../GlobalStateStore';
import { useGetIsGlobalPageEntity } from '../InterfaceStore/builder/getters';

/**
 * for the use cases where based on the dataSourceId, we need to get the data source state actions, which can be either the app level data source actions or the page level data source actions
 */
export function useGetDataSourceStateActions() {
  const { getDataSourceRecord } = useGetDataSourceRecord();
  const dataSourceActions = useGlobalStateStore().hooks.useActions().dataSource;
  const appDataSourceActions = useGlobalAppStateStore().hooks.useActions().dataSource;

  const getDataSourceStateActions = useCallback(
    (dataSourceId: string) => {
      // because entity data source is not stored in the data source record store
      // we know that when data source id is an entity type, then it must be page level data source
      if (getIsDataSourceAnEntityType(dataSourceId)) {
        return dataSourceActions;
      }
      const dataSource = getDataSourceRecord(dataSourceId);
      const isAppDataSource = DataSourceHelper.isAppDataSource(dataSource);
      return isAppDataSource ? appDataSourceActions : dataSourceActions;
    },
    [dataSourceActions, getDataSourceRecord, appDataSourceActions],
  );

  return { getDataSourceStateActions };
}

/**
 * for the use cases where based on the dataSourceId, we need to get the global state store state, which can be either the app level global state store or the page level global state store
 */
export function useGetGlobalStateStoreStateFromDataSourceId() {
  const { getGlobalStateStoreState } = useGetGlobalStateStoreState();
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const { getDataSourceRecord } = useGetDataSourceRecord();

  const getGlobalStateStoreStateFromDataSourceId = useCallback(
    (dataSourceId: string) => {
      // because entity data source is not stored in the data source record store
      // we know that when data source id is an entity type, then it must be page level data source
      if (getIsDataSourceAnEntityType(dataSourceId)) {
        return getGlobalStateStoreState();
      }
      const dataSource = getDataSourceRecord(dataSourceId);
      if (!dataSource) return;
      const isAppDataSource = DataSourceHelper.isAppDataSource(dataSource);
      return isAppDataSource ? getGlobalAppStateStoreState() : getGlobalStateStoreState();
    },
    [getGlobalStateStoreState, getGlobalAppStateStoreState, getDataSourceRecord],
  );

  return { getGlobalStateStoreStateFromDataSourceId };
}

export function useGetGlobalStateStoreStateByEntity() {
  const { getGlobalStateStoreState: getGlobalPageStateStoreState } = useGetGlobalStateStoreState();
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const { getIsGlobalPageEntity } = useGetIsGlobalPageEntity();

  const getGlobalStateStoreState = useCallback(
    ({ entityId, type }: { entityId: string; type: EntityTypes }) => {
      const isGlobalPageEntity = getIsGlobalPageEntity({ entityId, type });
      return isGlobalPageEntity ? getGlobalAppStateStoreState() : getGlobalPageStateStoreState();
    },
    [getGlobalPageStateStoreState, getGlobalAppStateStoreState, getIsGlobalPageEntity],
  );

  return { getGlobalStateStoreState };
}

export function useGetGlobalStateStoreActionsByEntity() {
  const { getGlobalStateStoreState } = useGetGlobalStateStoreStateByEntity();

  const getGlobalStateStoreStateActions = useCallback(
    ({ entityId, type }: { entityId: string; type: EntityTypes }) => {
      return getGlobalStateStoreState({ entityId, type }).actions;
    },
    [getGlobalStateStoreState],
  );

  return { getGlobalStateStoreStateActions };
}
